<script setup>
import { ref, watch, computed, onUnmounted } from "vue";
import welcomeTypoLogo from "@/assets/logo-new-typo-dark.svg";
import { IDIOMA, enIdioma } from "@/i18n/idioma";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["accept"]);

const view = ref("welcome");

const backgroundStyle = computed(() => ({
  "--welcome-modal-logo": `url("${welcomeTypoLogo}")`,
}));

const openTerms = () => {
  view.value = "terms";
};

const backToWelcome = () => {
  view.value = "welcome";
};

const acceptWelcome = () => {
  emit("accept");
};

const handleKeydown = (event) => {
  // Keep acceptance mandatory: only navigate back with Escape from terms.
  if (event.key === "Escape" && view.value === "terms") {
    backToWelcome();
  }
};

watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeydown);
      document.body.style.overflow = "hidden";
      view.value = "welcome";
      return;
    }

    document.removeEventListener("keydown", handleKeydown);
    document.body.style.overflow = "";
  },
  { immediate: true },
);

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  document.body.style.overflow = "";
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="modal-backdrop" role="dialog" aria-modal="true">
        <div
          class="welcome-modal"
          :class="{ 'welcome-modal-terms': view === 'terms' }"
          :style="backgroundStyle"
        >
          <div v-if="view === 'welcome'" class="welcome-modal-content">
            <header class="welcome-header">
              <div class="welcome-header-copy">
                <h2>{{ t("marco.bienvenida.titulo") }}</h2>
                <p class="welcome-subtitle">{{ t("marco.bienvenida.lema") }}</p>
              </div>
            </header>

            <!-- Astra FM, no Astralab (Pablo, 26.10.26): la marca anterior seguía
                 aquí, y Google lo ve encima de cada página al renderizarla. -->
            <p>{{ t("marco.bienvenida.radio") }}</p>
            <p>{{ t("marco.bienvenida.edad") }}</p>
            <p>
              <i18n-t keypath="marco.bienvenida.aceptas" scope="global">
                <template #terminos>
                  <button class="welcome-terms-link" @click="openTerms">{{
                    t("marco.bienvenida.terminos")
                  }}</button>
                </template>
              </i18n-t>
            </p>

            <button class="welcome-enter-btn" @click="acceptWelcome">
              {{ t("marco.bienvenida.entrar") }}
            </button>
          </div>

          <div v-else class="welcome-modal-content welcome-terms-content">
            <header class="welcome-header welcome-header-terms">
              <div class="welcome-header-copy">
                <!-- ⚠️ Los términos de aquí abajo se traducen en la tanda 4, con las
                     páginas legales (docs/IDIOMAS.md). -->
                <h2>Terminos y condiciones</h2>
                <p class="welcome-subtitle">{{ t("marco.bienvenida.lema") }}</p>
              </div>
            </header>

            <div class="welcome-terms-scroll">
              <section>
                <h3>1. Aceptacion de los terminos</h3>
                <p>
                  Al acceder y utilizar Astra FM (en adelante, "el Servicio"),
                  aceptas estos Terminos y Condiciones. Si no estas de acuerdo
                  con alguna parte de estos terminos, no debes utilizar nuestro
                  servicio.
                </p>
              </section>

              <section>
                <h3>2. Restriccion de edad</h3>
                <p>
                  <strong
                    >Astra FM esta dirigida exclusivamente a mayores de 18
                    anos.</strong
                  >
                  El contenido de nuestras emisiones, musica y programas puede
                  incluir tematicas, lenguaje y referencias destinadas a un
                  publico adulto. Al utilizar este servicio, declaras y
                  garantizas que eres mayor de 18 anos.
                </p>
              </section>

              <section>
                <h3>3. Descripcion del servicio</h3>
                <p>
                  Astra FM es una emisora de radio online que ofrece streaming
                  de musica alternativa y programas especializados. El servicio
                  incluye:
                </p>
                <ul>
                  <li>Transmision de audio en directo 24/7</li>
                  <li>Sistema de votacion de musica ("Riot")</li>
                  <li>Noticias y contenido musical actualizado</li>
                  <li>
                    Interaccion con la comunidad a traves de redes sociales
                  </li>
                </ul>
              </section>

              <section>
                <h3>4. Propiedad intelectual</h3>
                <p>
                  Todo el contenido disponible en Astra FM, incluyendo pero no
                  limitado a textos, graficos, logos, imagenes, clips de audio y
                  software, es propiedad de Astra FM o de sus proveedores de
                  contenido y esta protegido por las leyes de propiedad
                  intelectual.
                </p>
                <p>
                  La musica emitida esta licenciada a traves de la SGAE y otros
                  organismos de gestion de derechos correspondientes.
                </p>
              </section>

              <section>
                <h3>5. Uso permitido</h3>
                <p>Te comprometes a utilizar el servicio unicamente para:</p>
                <ul>
                  <li>Escuchar musica y contenido de audio</li>
                  <li>Participar en el sistema de votacion</li>
                  <li>Interactuar con la comunidad de manera respetuosa</li>
                  <li>
                    Compartir contenido en redes sociales mediante nuestras
                    herramientas
                  </li>
                </ul>
              </section>

              <section>
                <h3>6. Conductas prohibidas</h3>
                <p>Esta expresamente prohibido:</p>
                <ul>
                  <li>
                    Copiar, distribuir o modificar el contenido sin autorizacion
                  </li>
                  <li>
                    Utilizar el servicio para actividades ilegales o no
                    autorizadas
                  </li>
                  <li>Intentar acceder a areas restringidas del sistema</li>
                  <li>Manipular o interferir con el sistema de votacion</li>
                  <li>
                    Realizar ingenieria inversa del software o la plataforma
                  </li>
                  <li>
                    Utilizar bots o scripts automatizados sin autorizacion
                  </li>
                </ul>
              </section>

              <section>
                <h3>7. Sistema de votacion (Riot)</h3>
                <p>
                  El sistema "Riot" permite a los usuarios votar las canciones
                  emitidas. Al participar, aceptas que:
                </p>
                <ul>
                  <li>
                    Los votos son anonimos pero pueden ser rastreados para
                    prevenir fraude
                  </li>
                  <li>
                    Astra FM se reserva el derecho de validar o invalidar votos
                  </li>
                  <li>
                    Los resultados pueden ser utilizados para analisis y mejoras
                    del servicio
                  </li>
                </ul>
              </section>

              <section>
                <h3>8. Privacidad y datos</h3>
                <p>
                  Revisa nuestra
                  <a
                    :href="enIdioma('/legal/privacy', IDIOMA)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    politica de privacidad
                  </a>
                  para conocer como tratamos tus datos.
                </p>
              </section>

              <section>
                <h3>9. Modificaciones del servicio</h3>
                <p>
                  Astra FM se reserva el derecho de modificar, suspender o
                  descontinuar cualquier aspecto del servicio en cualquier
                  momento, con o sin previo aviso. No seremos responsables ante
                  ti ni ante terceros por cualquier modificacion, suspension o
                  interrupcion del servicio.
                </p>
              </section>

              <section>
                <h3>10. Limitacion de responsabilidad</h3>
                <p>
                  El servicio se proporciona "tal cual" y "segun
                  disponibilidad". Astra FM no garantiza que:
                </p>
                <ul>
                  <li>
                    El servicio estara disponible de forma ininterrumpida o
                    libre de errores
                  </li>
                  <li>La calidad del audio sera constante</li>
                  <li>
                    Los resultados obtenidos del uso del servicio seran precisos
                  </li>
                </ul>
                <p>
                  En ningun caso Astra FM sera responsable de danos directos,
                  indirectos, incidentales o consecuentes derivados del uso o la
                  imposibilidad de uso del servicio.
                </p>
              </section>

              <section>
                <h3>11. Enlaces a terceros</h3>
                <p>
                  Nuestro servicio puede contener enlaces a sitios web o
                  servicios de terceros (Spotify, Bluesky, etc.). Astra FM no
                  controla ni es responsable del contenido, politicas de
                  privacidad o practicas de sitios web de terceros.
                </p>
              </section>

              <section>
                <h3>12. Legislacion aplicable</h3>
                <p>
                  Estos Terminos y Condiciones se rigen por las leyes de Espana.
                  Cualquier disputa relacionada con estos terminos estara sujeta
                  a la jurisdiccion exclusiva de los tribunales espanoles.
                </p>
              </section>

              <section>
                <h3>13. Contacto</h3>
                <p>
                  Si tienes dudas, visita
                  <a
                    :href="enIdioma('/legal/contact', IDIOMA)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    nuestra pagina de contacto
                  </a>
                  .
                </p>
              </section>
            </div>

            <button
              class="welcome-back-btn welcome-back-btn-floating"
              @click="backToWelcome"
            >
              {{ t("marco.bienvenida.volver") }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
@import "@/assets/scss/astra-vars.scss";

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 10000;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(5px);
}

.welcome-modal {
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 760px;
  max-height: calc(100vh - 2rem);
  background: var(--bg-color);
  border: 1px solid var(--border);
  border-radius: 0;
}

.welcome-modal::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(
      145deg,
      rgba(12, 18, 26, 0.86) 0%,
      rgba(12, 18, 26, 0.64) 100%
    ),
    var(--welcome-modal-logo);
  background-repeat: no-repeat;
  background-position: center 42%;
  background-size: min(85%, 560px) auto;
  opacity: 0.16;
  pointer-events: none;
}

html.light .welcome-modal::before {
  background-image:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.93) 0%,
      rgba(249, 250, 252, 0.9) 100%
    ),
    var(--welcome-modal-logo);
  opacity: 0.28;
}

.welcome-modal-terms {
  max-width: 980px;
}

.welcome-modal-content {
  position: relative;
  z-index: 1;
  overflow-y: auto;
  max-height: calc(100vh - 2rem);
  padding: $s34;

  h2 {
    margin: 0 0 $s16;
    color: var(--text-color);
    font-size: $s34;
    line-height: 1.1;
  }

  p {
    margin: 0 0 $s12;
    color: var(--text-color);
    line-height: 1.6;
    opacity: 0.9;
  }
}

.welcome-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: $s24;
  padding-bottom: $s16;
  border-bottom: 1px solid var(--border);
}

.welcome-header-copy {
  min-width: 0;
}

.welcome-subtitle {
  margin: $s04 0 0 !important;
  font-size: $s14;
  font-style: italic;
  opacity: 0.72;
}

.welcome-terms-link {
  // 🔴 En línea con el texto: un estilo global de los botones lo ponía en
  // `display: flex` y el enlace y el punto final caían cada uno en su línea.
  display: inline;
  border: 0;
  background: transparent;
  color: var(--accent-color);
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  line-height: inherit;
}

.welcome-enter-btn,
.welcome-back-btn {
  margin-top: $s18;
  border: 1px solid var(--border);
  border-radius: 0;
  background: var(--accent-color);
  color: #fff;
  padding: $s10 $s18;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    filter 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
}

.welcome-back-btn {
  background: var(--bg-color-grid);
  color: var(--text-color);
}

.welcome-terms-content {
  position: relative;
  padding-bottom: $s42;
}

.welcome-terms-scroll {
  border: 1px solid var(--border);
  border-radius: 0;
  overflow-y: auto;
  height: 58vh;
  max-height: 620px;
  padding: $s16;
  background: var(--bg-color-grid);

  section {
    margin-bottom: $s16;
  }

  h3 {
    margin: 0 0 $s08;
    color: var(--accent-color);
    font-size: $s16;
  }

  ul {
    margin: 0 0 $s12;
    padding-left: $s24;
    color: var(--text-color);
  }

  a {
    color: var(--accent-color);
    text-decoration: underline;
  }
}

.welcome-back-btn-floating {
  position: absolute;
  right: $s24;
  bottom: $s18;
  z-index: 2;
}

@media (max-width: 768px) {
  .welcome-modal-terms {
    max-width: 100%;
  }

  .welcome-modal-content {
    padding: $s18;

    h2 {
      font-size: $s24;
    }
  }

  .welcome-terms-scroll {
    height: 52vh;
  }

  .welcome-back-btn-floating {
    right: $s16;
    bottom: $s16;
  }
}
</style>
